import { FieldValues } from "react-hook-form";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

/**
 * CLE-2842 step 2 — the applicability the MPPR / Case Rate / Package forms hold: the
 * `applicability` field, else the legacy `isDefault` checkbox (checked = a Typical template).
 */
export const formApplicability = (props: FieldValues): RulesetApplicability =>
  props.applicability ?? (props.isDefault ? "TYPICAL" : "SCOPED");

/**
 * The scope part of a single-row body. A Typical template keeps the legacy `isDefault: true` and
 * no provider/payer, exactly as before. A Fallback rule has no provider and an optional payer
 * (`fallbackPayerId`, null = All payers). A Payer-Provider rule has its provider and its payer,
 * which may be null (All payers).
 */
const scopeFields = (props: FieldValues) => {
  const applicability = formApplicability(props);
  if (applicability === "TYPICAL") return { isDefault: true as const };
  if (applicability === "FALLBACK") {
    return { providerId: null, payerId: props.fallbackPayerId ?? null };
  }
  return {
    providerId: props.provider?.value ?? null,
    payerId: props.payerName?.value ?? null,
  };
};

const normalizeContractLanguage = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : null;
};

export const mapFormToRequestBody = (props: FieldValues) => ({
  cptCodes: props.cptCodes,
  discountPercentForRank2: Number(props.discountPercentForRank2),
  discountPercentForRank3: Number(props.discountPercentForRank3),
  discountPercentForRank4: Number(props.discountPercentForRank4),
  discountPercentForRank5Plus: Number(props.discountPercentForRank5Plus),
  groupName: props.groupName,
  ...scopeFields(props),
  allowOnDuplicateCptCodes: props.allowOnDuplicateCptCodes,
  duplicateNotPaid: props.duplicateNotPaid,
  contractLanguage: normalizeContractLanguage(props.contractLanguage),
});

export const mapCaseRateFormToRequestBody = (props: FieldValues) => ({
  cptCodes: props.cptCodes,
  groupName: props.groupName,
  ...scopeFields(props),
  excludedCptCodes: props.excludedCptCodes,
  isPerDiem: props.isPerDiem ?? false,
  isBilateral: props.isBilateral ?? false,
  contractLanguage: normalizeContractLanguage(props.contractLanguage),
});

export const mapPackagesFormToRequestBody = (props: FieldValues) => ({
  triggerCptCodes: props.triggerCptCodes,
  bundledCptCodes: props.bundledCptCodes,
  groupName: props.groupName,
  ...scopeFields(props),
  contractLanguage: normalizeContractLanguage(props.contractLanguage),
});

export const getRulesetList = (data: any, addDiscounts = true) => {
  const result = [] as any[];
  const { allowOnDuplicateCptCodes, duplicateNotPaid } = data;
  const discountsToAdd = {
    discountPercentForRank2: data.discountPercentForRank2,
    discountPercentForRank3: data.discountPercentForRank3,
    discountPercentForRank4: data.discountPercentForRank4,
    discountPercentForRank5Plus: data.discountPercentForRank5Plus,
  };
  // Typical and Fallback rules have no provider/payer cross-product — emit a single entry. A
  // Fallback rule carries its one payer (or All payers) in `fallbackPayerId`.
  const applicability = formApplicability(data);
  if (applicability !== "SCOPED") {
    result.push({
      applicability,
      isDefault: true,
      fallbackPayerId: data.fallbackPayerId ?? null,
      groupName: data.groupName,
      cptCodes: data.cptCodes,
      excludedCptCodes: data.excludedCptCodes,
      isPerDiem: data.isPerDiem,
      isBilateral: data.isBilateral,
      triggerCptCodes: data.triggerCptCodes,
      bundledCptCodes: data.bundledCptCodes,
      allowOnDuplicateCptCodes,
      duplicateNotPaid,
      contractLanguage: data.contractLanguage,
      ...(addDiscounts && discountsToAdd),
    });
    return result;
  }
  // A Payer-Provider rule fans out provider × payer. "All payers" is one payer here (value
  // null): one row per provider with payerId null — it is never combined with named payers.
  const providers = data.provider;
  const payers = data.payerName;
  providers.forEach((provider: any) => {
    payers.forEach((payer: any) => {
      const newObj = {
        applicability,
        provider: {
          name: provider.name,
          value: provider.value,
        },
        payerName: {
          name: payer.name,
          value: payer.value,
        },
        groupName: data.groupName,
        cptCodes: data.cptCodes,
        excludedCptCodes: data.excludedCptCodes,
        isPerDiem: data.isPerDiem,
        isBilateral: data.isBilateral,
        triggerCptCodes: data.triggerCptCodes,
        bundledCptCodes: data.bundledCptCodes,
        allowOnDuplicateCptCodes,
        duplicateNotPaid,
        contractLanguage: data.contractLanguage,
        ...(addDiscounts && discountsToAdd),
      };
      result.push(newObj);
    });
  });
  return result;
};

/**
 * CLE-2842 — the fields a group update must NOT send: a bucket is one provider, its payers are
 * edited through `addPayerIds` / `removeIds`, applicability is immutable, and contract language
 * is kept per payer (like attachments), so a bucket save never touches it. The server answers 400
 * if any of them is present.
 */
type TGroupUpdateScopeField =
  | "providerId"
  | "payerId"
  | "payerName"
  | "applicability"
  | "isDefault"
  | "contractLanguage";

export const GROUP_UPDATE_SCOPE_FIELDS: TGroupUpdateScopeField[] = [
  "providerId",
  "payerId",
  "payerName",
  "applicability",
  "isDefault",
  "contractLanguage",
];

/** A single-row request body (from the mappers above) reshaped as a group update's `data`. */
export const omitScopeFields = <TBody extends Record<string, unknown>>(
  body: TBody
) => {
  const data: Record<string, unknown> = { ...body };
  GROUP_UPDATE_SCOPE_FIELDS.forEach((field) => {
    delete data[field];
  });
  return data as Omit<TBody, TGroupUpdateScopeField>;
};
